/**
 * Auth request schemas — mirror openapi.yaml /auth/* request bodies exactly.
 * The server validates with these; the web client derives its form types here.
 */
import { z } from "zod";
import { ianaTimezone, isoDate } from "./common.js";

/** POST /auth/dob-gate (unauthenticated, 10/h per IP). */
export const dobGateBody = z.object({
  dob: isoDate,
  device_fp: z.string().min(1).max(256),
});
export type DobGateBody = z.infer<typeof dobGateBody>;

/** POST /auth/signup — always 202, no enumeration. */
export const signupBody = z.object({
  email: z.string().email().max(320),
  password: z.string().min(10).max(512),
  dob: isoDate,
  device_fp: z.string().min(1).max(256),
  tz: ianaTimezone,
  /** optional; E2E-cohort signup, server env-gated (ARCH §9). */
  e2e_key: z.string().max(256).optional(),
});
export type SignupBody = z.infer<typeof signupBody>;

/** POST /auth/login — 5/15 min per IP+email. */
export const loginBody = z.object({
  email: z.string().email().max(320),
  password: z.string().min(1).max(512),
  device_fp: z.string().min(1).max(256),
});
export type LoginBody = z.infer<typeof loginBody>;

/** POST /auth/google — code flow + PKCE; dob required on the new-account path (412 otherwise). */
export const googleBody = z.object({
  code: z.string().min(1).max(2048),
  code_verifier: z.string().min(1).max(256),
  device_fp: z.string().min(1).max(256),
  dob: isoDate.optional(),
  tz: ianaTimezone.optional(),
});
export type GoogleBody = z.infer<typeof googleBody>;

/** POST /auth/magic-link — always 202; 3/h per email + 10/h per IP. */
export const magicLinkRequestBody = z.object({
  email: z.string().email().max(320),
});
export type MagicLinkRequestBody = z.infer<typeof magicLinkRequestBody>;

/** POST /auth/magic-link/consume — 10/h per IP. */
export const magicLinkConsumeBody = z.object({
  token: z.string().min(22).max(512), // ≥128-bit CSPRNG entropy pinned (ARCH §6)
  device_fp: z.string().min(1).max(256),
});
export type MagicLinkConsumeBody = z.infer<typeof magicLinkConsumeBody>;

/** POST /auth/verify-email/consume — 10/h per IP. */
export const verifyEmailConsumeBody = z.object({
  token: z.string().min(22).max(512),
});
export type VerifyEmailConsumeBody = z.infer<typeof verifyEmailConsumeBody>;

/** POST /auth/logout. */
export const logoutBody = z
  .object({
    all: z.boolean().default(false),
  })
  .default({ all: false });
export type LogoutBody = z.infer<typeof logoutBody>;

/** POST /auth/reauth — exactly one proof; 5-min reauth token back. */
export const reauthBody = z
  .object({
    password: z.string().min(1).max(512).optional(),
    google_code: z.string().min(1).max(2048).optional(),
    magic_token: z.string().min(22).max(512).optional(),
  })
  .refine(
    (b) => [b.password, b.google_code, b.magic_token].filter((x) => x !== undefined).length === 1,
    "exactly one credential proof is required",
  );
export type ReauthBody = z.infer<typeof reauthBody>;

/** PUT /auth/password — add or change; bumps auth_version (revocation completeness). */
export const passwordPutBody = z.object({
  reauth_token: z.string().min(22).max(512),
  new_password: z.string().min(10).max(512),
});
export type PasswordPutBody = z.infer<typeof passwordPutBody>;

/** Access-token claims (openapi bearerAuth description). */
export const accessClaims = z.object({
  sub: z.string().uuid(),
  role: z.enum(["user", "moderator", "admin"]),
  auth_version: z.number().int().min(1),
  seed_cohort: z.string().nullable().optional(),
});
export type AccessClaims = z.infer<typeof accessClaims>;
